import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ADDABLE_ACTIVITIES,
  TIMELINE_SLOTS,
  blockOverlapsAny,
  buildTimelineSegments,
  cloneInitialSchedule,
  getDurationLimitsForBlock,
  getGapStartMinFromSegment,
  getAddableActivityByKind,
  canEditBlockDuration,
  syncScheduleLayout,
  sortActivitiesForSidebar,
  validateCalendarSchedule,
} from "./stage2CalendarData";
import { isMobViewport } from "./gameStageBackground";

const DRAG_MIME = "application/x-stage2-drag";
const GAP_TOUCH_PADDING = 24;
const AUTO_SCROLL_EDGE_PX = 80;
const AUTO_SCROLL_MAX_STEP = 22;

function parseDragPayload(raw) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function segmentFlexStyle(weight) {
  return {
    flexGrow: weight,
    flexBasis: 0,
    "--segment-weight": weight,
  };
}

function findGapElementAt(clientX, clientY, { activeOnly = false } = {}) {
  const elements = document.elementsFromPoint(clientX, clientY);
  for (const el of elements) {
    const gap = el.closest?.("[data-gap-start]");
    if (!gap) continue;
    if (activeOnly && !gap.classList.contains("stage2-cal__timeline-gap--active")) {
      continue;
    }
    return gap;
  }

  const gaps = document.querySelectorAll("[data-gap-start]");
  let bestGap = null;
  let bestDistance = Infinity;

  gaps.forEach((gap) => {
    if (activeOnly && !gap.classList.contains("stage2-cal__timeline-gap--active")) {
      return;
    }

    const rect = gap.getBoundingClientRect();
    if (!rect.width && !rect.height) return;

    const pad = GAP_TOUCH_PADDING;
    const insideExpanded =
      clientX >= rect.left - pad &&
      clientX <= rect.right + pad &&
      clientY >= rect.top - pad &&
      clientY <= rect.bottom + pad;

    if (!insideExpanded) return;

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const distance = Math.hypot(clientX - centerX, clientY - centerY);

    if (distance < bestDistance) {
      bestDistance = distance;
      bestGap = gap;
    }
  });

  return bestGap;
}

function getAutoScrollSpeed(clientY) {
  const viewportHeight = window.innerHeight;
  if (clientY < AUTO_SCROLL_EDGE_PX) {
    return (
      -((AUTO_SCROLL_EDGE_PX - clientY) / AUTO_SCROLL_EDGE_PX) * AUTO_SCROLL_MAX_STEP
    );
  }
  if (clientY > viewportHeight - AUTO_SCROLL_EDGE_PX) {
    return (
      ((clientY - (viewportHeight - AUTO_SCROLL_EDGE_PX)) / AUTO_SCROLL_EDGE_PX) *
      AUTO_SCROLL_MAX_STEP
    );
  }
  return 0;
}

function getScrollableAncestors(startEl) {
  const targets = [];
  let node = startEl;

  while (node) {
    if (node === document.body || node === document.documentElement) break;
    const style = window.getComputedStyle(node);
    const canScrollY =
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      node.scrollHeight > node.clientHeight + 1;
    if (canScrollY) targets.push(node);
    node = node.parentElement;
  }

  const doc = document.scrollingElement || document.documentElement;
  if (doc.scrollHeight > doc.clientHeight + 1 && !targets.includes(doc)) {
    targets.push(doc);
  }

  return targets;
}

function DurationStepper({ value, min, max, step, onChange, disabled }) {
  return (
    <div className="stage2-cal__stepper">
      <button
        type="button"
        className="stage2-cal__stepper-btn stage2-cal__stepper-btn--minus"
        disabled={disabled || value <= min}
        onClick={() => onChange(Math.max(min, value - step))}
        aria-label="Decrease duration"
      >
        −
      </button>
      <span className="stage2-cal__stepper-val">{value} min</span>
      <button
        type="button"
        className="stage2-cal__stepper-btn stage2-cal__stepper-btn--plus"
        disabled={disabled || value >= max}
        onClick={() => onChange(Math.min(max, value + step))}
        aria-label="Increase duration"
      >
        +
      </button>
    </div>
  );
}

function DraggableActivityCard({
  activity,
  isDragging,
  usePointerDrag,
  onDragStart,
  onDragEnd,
  onPointerDragStart,
}) {
  const duration = activity.defaultDuration;

  return (
    <div
      className={`stage2-cal__activity-card stage2-cal__activity-card--draggable${
        isDragging ? " stage2-cal__activity-card--dragging" : ""
      }`}
      draggable={!usePointerDrag}
      onDragStart={(e) => {
        if (usePointerDrag) {
          e.preventDefault();
          return;
        }
        const payload = { type: "add", kind: activity.kind, duration };
        e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
        e.dataTransfer.effectAllowed = "copy";
        onDragStart(payload);
      }}
      onDragEnd={onDragEnd}
      onPointerDown={(e) => {
        if (!usePointerDrag || !e.isPrimary) {
          return;
        }
        e.preventDefault();
        onPointerDragStart?.(
          { type: "add", kind: activity.kind, duration },
          e,
          e.currentTarget
        );
      }}
    >
      <span
        className="stage2-cal__activity-icon"
        style={{
          backgroundColor: `${activity.color}22`,
          color: activity.color,
        }}
        aria-hidden
      >
        {activity.icon}
      </span>
      <div className="stage2-cal__activity-body">
        <span className="stage2-cal__activity-label">{activity.label}</span>
        <span className="stage2-cal__activity-duration">{duration} min</span>
      </div>
      <span className="stage2-cal__activity-grip" aria-hidden title="Drag to calendar">
        ⠿
      </span>
    </div>
  );
}

function ScheduleBlock({
  block,
  flexGrow,
  showDurationStepper = false,
  onDurationChange,
  onRemove,
}) {
  const canRemove = block.kind !== "meeting";
  const canEditDuration = canEditBlockDuration(block);
  const limits = getDurationLimitsForBlock(block);

  return (
    <div className="stage2-cal__block-row" style={segmentFlexStyle(flexGrow)}>
      <div
        className={`stage2-cal__block-inner${
          showDurationStepper && canEditDuration
            ? " stage2-cal__block-inner--with-stepper"
            : ""
        }`}
        style={{ "--block-color": block.color }}
      >
        <span className="stage2-cal__block-icon" aria-hidden>
          {block.icon}
        </span>
        <span className="stage2-cal__block-label">{block.label}</span>
        {showDurationStepper && canEditDuration ? (
          <div
            className="stage2-cal__block-stepper"
            onPointerDown={(e) => e.stopPropagation()}
          >
            <DurationStepper
              value={block.duration}
              min={limits.min}
              max={limits.max}
              step={limits.step}
              onChange={(duration) => onDurationChange?.(block.id, duration)}
            />
          </div>
        ) : (
          <span className="stage2-cal__block-duration">{block.duration} min</span>
        )}
        {canRemove ? (
          <button
            type="button"
            className="stage2-cal__block-remove"
            onClick={() => onRemove(block.id)}
            aria-label={`Remove ${block.label}`}
          >
            ×
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default function Stage2CalendarOptimize({
  onSubmit,
  submitting = false,
}) {
  const [schedule, setSchedule] = useState(() =>
    syncScheduleLayout(cloneInitialSchedule())
  );
  const [feedback, setFeedback] = useState(null);
  const [showInsights, setShowInsights] = useState(false);
  const [dragState, setDragState] = useState(null);
  const [activeDropGap, setActiveDropGap] = useState(null);

  const dragRef = useRef(null);
  const pointerCleanupRef = useRef(null);
  const dragFloatRef = useRef(null);
  const calRootRef = useRef(null);
  const autoScrollRafRef = useRef(null);
  const pointerPosRef = useRef({ x: 0, y: 0 });

  const [isMobilePortrait, setIsMobilePortrait] = useState(() => isMobViewport());

  useEffect(() => {
    const syncViewport = () => setIsMobilePortrait(isMobViewport());
    syncViewport();
    window.addEventListener("resize", syncViewport);
    window.addEventListener("orientationchange", syncViewport);
    return () => {
      window.removeEventListener("resize", syncViewport);
      window.removeEventListener("orientationchange", syncViewport);
    };
  }, []);

  const usePointerDrag = useMemo(() => {
    if (isMobilePortrait) return true;
    if (typeof window === "undefined") return false;
    return "ontouchstart" in window && window.innerWidth <= 900;
  }, [isMobilePortrait]);

  const validation = useMemo(() => validateCalendarSchedule(schedule), [schedule]);
  const sidebarActivities = useMemo(
    () => sortActivitiesForSidebar(schedule),
    [schedule]
  );

  const addedBreakKinds = useMemo(
    () => new Set(schedule.filter((b) => b.kind !== "meeting").map((b) => b.kind)),
    [schedule]
  );

  const availableActivities = useMemo(
    () => ADDABLE_ACTIVITIES.filter((activity) => !addedBreakKinds.has(activity.kind)),
    [addedBreakKinds]
  );

  const timelineSegments = useMemo(
    () => buildTimelineSegments(schedule),
    [schedule]
  );

  const scoreMessage = useMemo(() => {
    if (validation.valid && validation.score >= 85) {
      return "Great Balance! You have a productive and well-balanced day.";
    }
    if (validation.score >= 70) {
      return "Good progress — keep balancing focus and breaks.";
    }
    return "Keep optimizing your day.";
  }, [validation]);

  const clearDragFloat = useCallback(() => {
    dragFloatRef.current?.remove();
    dragFloatRef.current = null;
  }, []);

  const stopAutoScrollLoop = useCallback(() => {
    if (autoScrollRafRef.current != null) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const finishDrag = useCallback(() => {
    stopAutoScrollLoop();
    pointerCleanupRef.current?.();
    pointerCleanupRef.current = null;
    clearDragFloat();
    dragRef.current = null;
    setDragState(null);
    setActiveDropGap(null);
  }, [clearDragFloat, stopAutoScrollLoop]);

  const gapFitsDrag = useCallback((gapDuration, drag) => {
    if (!drag?.duration) return false;
    return gapDuration >= drag.duration;
  }, []);

  const setGapHighlightFromPoint = useCallback(
    (clientX, clientY) => {
      const gapEl = findGapElementAt(clientX, clientY, { activeOnly: true });
      if (!gapEl || !dragRef.current) {
        setActiveDropGap(null);
        return null;
      }
      const gapDuration = Number(gapEl.dataset.gapDuration);
      const gapKey = gapEl.dataset.gapKey;
      if (gapFitsDrag(gapDuration, dragRef.current)) {
        setActiveDropGap(gapKey || null);
        return gapEl;
      }
      setActiveDropGap(null);
      return null;
    },
    [gapFitsDrag]
  );

  const mountDragFloat = useCallback((sourceEl, clientX, clientY) => {
    clearDragFloat();
    const rect = sourceEl.getBoundingClientRect();
    const float = sourceEl.cloneNode(true);
    float.classList.add("stage2-cal__drag-float");
    float.style.width = `${rect.width}px`;
    float.style.left = `${clientX - rect.width / 2}px`;
    float.style.top = `${clientY - 20}px`;
    document.body.appendChild(float);
    dragFloatRef.current = float;
  }, [clearDragFloat]);

  const moveDragFloat = useCallback((clientX, clientY) => {
    if (!dragFloatRef.current) return;
    const width = dragFloatRef.current.offsetWidth;
    dragFloatRef.current.style.left = `${clientX - width / 2}px`;
    dragFloatRef.current.style.top = `${clientY - 20}px`;
  }, []);

  const runAutoScrollLoop = useCallback(() => {
    autoScrollRafRef.current = null;
    if (!dragRef.current) return;

    const { x, y } = pointerPosRef.current;
    const speed = getAutoScrollSpeed(y);

    if (speed !== 0) {
      const targets = getScrollableAncestors(calRootRef.current);
      for (const target of targets) {
        const maxScroll = target.scrollHeight - target.clientHeight;
        target.scrollTop = Math.max(0, Math.min(maxScroll, target.scrollTop + speed));
      }
      moveDragFloat(x, y);
      setGapHighlightFromPoint(x, y);
      autoScrollRafRef.current = requestAnimationFrame(runAutoScrollLoop);
    }
  }, [moveDragFloat, setGapHighlightFromPoint]);

  const queueAutoScrollLoop = useCallback(() => {
    if (autoScrollRafRef.current != null) return;
    if (getAutoScrollSpeed(pointerPosRef.current.y) !== 0) {
      autoScrollRafRef.current = requestAnimationFrame(runAutoScrollLoop);
    }
  }, [runAutoScrollLoop]);

  const applyDrop = useCallback(
    (segment, drag) => {
      const startMin = getGapStartMinFromSegment(segment);
      if (!drag || startMin == null) return;

      if (segment.duration < drag.duration) {
        setFeedback({
          type: "warn",
          message:
            "This slot is too short. Shorten a meeting or try a larger gap.",
        });
        return;
      }

      if (drag.type === "add" && drag.kind) {
        const activity = getAddableActivityByKind(drag.kind);
        if (!activity) return;

        setSchedule((prev) => {
          if (prev.some((b) => b.kind === activity.kind)) {
            setFeedback({
              type: "warn",
              message: `${activity.label} is already on your schedule.`,
            });
            return prev;
          }

          if (blockOverlapsAny(prev, startMin, drag.duration)) {
            setFeedback({
              type: "warn",
              message: "That slot overlaps another event.",
            });
            return prev;
          }

          const newBlock = {
            id: `${activity.id}_${Date.now()}`,
            kind: activity.kind,
            label: activity.label,
            startMin,
            duration: drag.duration,
            initialDuration: activity.defaultDuration,
            icon: activity.icon,
            color: activity.color,
            editable: false,
          };

          setFeedback(null);
          return syncScheduleLayout([...prev, newBlock]);
        });
      }
    },
    []
  );

  const applyDropFromGapElement = useCallback(
    (gapEl, drag) => {
      if (!gapEl || !drag) return;
      const segment = {
        key: gapEl.dataset.gapKey,
        duration: Number(gapEl.dataset.gapDuration),
        type: "gap",
      };
      applyDrop(segment, drag);
    },
    [applyDrop]
  );

  const handleBlockDurationChange = useCallback((blockId, duration) => {
    setSchedule((prev) => {
      const block = prev.find((b) => b.id === blockId);
      if (!block || !canEditBlockDuration(block)) return prev;

      const updated = prev.map((b) =>
        b.id === blockId ? { ...b, duration } : b
      );
      return syncScheduleLayout(updated);
    });
    setFeedback(null);
  }, []);

  const handleRemove = useCallback((blockId) => {
    setSchedule((prev) => syncScheduleLayout(prev.filter((b) => b.id !== blockId)));
    setFeedback(null);
  }, []);

  const handleDragStart = useCallback((payload) => {
    dragRef.current = payload;
    setDragState(payload);
    setActiveDropGap(null);
    setFeedback(null);
  }, []);

  const handleDragEnd = useCallback(() => {
    finishDrag();
  }, [finishDrag]);

  const handlePointerDragStart = useCallback(
    (payload, event, sourceEl) => {
      if (dragRef.current || !event.isPrimary) return;

      const pointerId = event.pointerId;
      const { clientX, clientY } = event;

      pointerPosRef.current = { x: clientX, y: clientY };
      dragRef.current = payload;
      setDragState(payload);
      setActiveDropGap(null);
      setFeedback(null);
      mountDragFloat(sourceEl, clientX, clientY);

      try {
        sourceEl.setPointerCapture(pointerId);
      } catch {
        /* ignore */
      }

      const onMove = (e) => {
        if (e.pointerId !== pointerId) return;
        e.preventDefault();
        pointerPosRef.current = { x: e.clientX, y: e.clientY };
        moveDragFloat(e.clientX, e.clientY);
        setGapHighlightFromPoint(e.clientX, e.clientY);
        queueAutoScrollLoop();
      };

      const onEnd = (e) => {
        if (e.pointerId !== pointerId) return;
        e.preventDefault();
        stopAutoScrollLoop();

        try {
          sourceEl.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }

        const gapEl = findGapElementAt(e.clientX, e.clientY, { activeOnly: true });
        const drag = dragRef.current;
        if (gapEl && drag) {
          const gapDuration = Number(gapEl.dataset.gapDuration);
          if (gapFitsDrag(gapDuration, drag)) {
            applyDropFromGapElement(gapEl, drag);
          }
        }
        finishDrag();
      };

      document.addEventListener("pointermove", onMove, { passive: false });
      document.addEventListener("pointerup", onEnd);
      document.addEventListener("pointercancel", onEnd);

      pointerCleanupRef.current = () => {
        stopAutoScrollLoop();
        document.removeEventListener("pointermove", onMove);
        document.removeEventListener("pointerup", onEnd);
        document.removeEventListener("pointercancel", onEnd);
        try {
          sourceEl.releasePointerCapture(pointerId);
        } catch {
          /* ignore */
        }
      };
    },
    [
      applyDropFromGapElement,
      finishDrag,
      gapFitsDrag,
      mountDragFloat,
      moveDragFloat,
      queueAutoScrollLoop,
      setGapHighlightFromPoint,
      stopAutoScrollLoop,
    ]
  );

  const handleGapDragOver = useCallback(
    (e, segment) => {
      const drag = dragRef.current;
      if (!drag) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = "copy";
      if (gapFitsDrag(segment.duration, drag)) {
        setActiveDropGap(segment.key);
      } else {
        setActiveDropGap(null);
      }
    },
    [gapFitsDrag]
  );

  const handleGapDrop = useCallback(
    (e, segment) => {
      e.preventDefault();
      e.stopPropagation();
      const payload =
        parseDragPayload(e.dataTransfer.getData(DRAG_MIME)) || dragRef.current;
      applyDrop(segment, payload);
      finishDrag();
    },
    [applyDrop, finishDrag]
  );

  const handleBlocksDragOver = useCallback((e) => {
    if (!dragRef.current) return;
    e.preventDefault();
    setGapHighlightFromPoint(e.clientX, e.clientY);
  }, [setGapHighlightFromPoint]);

  useEffect(() => {
    if (!usePointerDrag) return undefined;

    if (dragState) {
      document.body.classList.add("stage2-cal-drag-active");
    } else {
      document.body.classList.remove("stage2-cal-drag-active");
    }

    return () => {
      document.body.classList.remove("stage2-cal-drag-active");
    };
  }, [dragState, usePointerDrag]);

  useEffect(() => {
    if (!dragState || usePointerDrag) return undefined;

    const onDragOver = (e) => {
      e.preventDefault();
      setGapHighlightFromPoint(e.clientX, e.clientY);
    };

    document.addEventListener("dragover", onDragOver);
    return () => document.removeEventListener("dragover", onDragOver);
  }, [dragState, usePointerDrag, setGapHighlightFromPoint]);

  useEffect(() => {
    return () => {
      pointerCleanupRef.current?.();
      clearDragFloat();
    };
  }, [clearDragFloat]);

  const handleReset = () => {
    setSchedule(syncScheduleLayout(cloneInitialSchedule()));
    setShowInsights(false);
    setFeedback(null);
    finishDrag();
  };

  const handleSubmit = () => {
    const result = validateCalendarSchedule(schedule);
    if (!result.valid) {
      setFeedback({
        type: "error",
        message: result.missing.slice(0, 3).join(" · "),
      });
      return;
    }
    onSubmit?.(
      schedule.map(({ id, kind, label, startMin, duration }) => ({
        id,
        kind,
        label,
        startMin,
        duration,
      }))
    );
  };

  return (
    <div
      ref={calRootRef}
      className={`stage2-cal${
        dragState ? " stage2-cal--dragging" : ""
      }${isMobilePortrait ? " stage2-cal--mobile-portrait" : ""}`}
    >
      <header className="stage2-cal__header">
        <div className="stage2-cal__header-left">
          <span className="stage2-cal__back" aria-hidden>
            ←
          </span>
          <h2 className="stage2-cal__title">Calendar Optimization</h2>
          <button type="button" className="stage2-cal__help" aria-label="Help">
            ?
          </button>
        </div>
        <div className="stage2-cal__header-center">
          <p>
            Adjust activity durations to build the{" "}
            <strong>most productive schedule!</strong>
          </p>
        </div>
      </header>

      <div className="stage2-cal__layout">
        {(!isMobilePortrait || availableActivities.length > 0) ? (
        <aside className="stage2-cal__sidebar stage2-cal__sidebar--left">
          <h3 className="stage2-cal__panel-title stage2-cal__panel-title--bar">
            {isMobilePortrait ? "Breaks & Focus" : "Activities"}
          </h3>
          <div className="stage2-cal__activity-list">
            {!isMobilePortrait
              ? sidebarActivities.map((activity) => {
                  const limits = getDurationLimitsForBlock(activity);
                  const canEditDuration = canEditBlockDuration(activity);
                  return (
                    <div key={activity.id} className="stage2-cal__activity-card">
                      <span
                        className="stage2-cal__activity-icon"
                        style={{
                          backgroundColor: `${activity.color}22`,
                          color: activity.color,
                        }}
                        aria-hidden
                      >
                        {activity.icon}
                      </span>
                      <div className="stage2-cal__activity-body">
                        <span className="stage2-cal__activity-label">
                          {activity.label}
                        </span>
                        {canEditDuration ? (
                          <DurationStepper
                            value={activity.duration}
                            min={limits.min}
                            max={limits.max}
                            step={limits.step}
                            onChange={(d) =>
                              handleBlockDurationChange(activity.id, d)
                            }
                          />
                        ) : (
                          <span className="stage2-cal__activity-duration">
                            {activity.duration} min
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              : null}
            {availableActivities.length ? (
              <>
                {!isMobilePortrait ? (
                  <p className="stage2-cal__activity-section">Breaks &amp; Focus</p>
                ) : null}
                {availableActivities.map((activity) => (
                  <DraggableActivityCard
                    key={activity.id}
                    activity={activity}
                    isDragging={
                      dragState?.type === "add" && dragState.kind === activity.kind
                    }
                    usePointerDrag={usePointerDrag}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    onPointerDragStart={handlePointerDragStart}
                  />
                ))}
              </>
            ) : null}
          </div>
        </aside>
        ) : null}

        <section className="stage2-cal__timeline-wrap">
          <div className="stage2-cal__timeline-head">
            <span className="stage2-cal__timeline-title">
              <span className="stage2-cal__timeline-nav" aria-hidden>
                ‹
              </span>
              <span className="stage2-cal__timeline-day">
                <span aria-hidden>📅</span> TODAY
              </span>
              <span className="stage2-cal__timeline-nav" aria-hidden>
                ›
              </span>
            </span>
            <button type="button" className="stage2-cal__clear" onClick={handleReset}>
              Clear Schedule
            </button>
          </div>
          <div className="stage2-cal__timeline">
            <div className="stage2-cal__times">
              {TIMELINE_SLOTS.map((slot) => (
                <span key={slot} className="stage2-cal__time-label">
                  {slot}
                </span>
              ))}
            </div>
            <div
              className={`stage2-cal__blocks${
                dragState ? " stage2-cal__blocks--dragging" : ""
              }`}
              onDragOver={handleBlocksDragOver}
            >
              {timelineSegments.map((segment) => {
                const gapStart = getGapStartMinFromSegment(segment);
                return segment.type === "gap" ? (
                  <div
                    key={segment.key}
                    className={`stage2-cal__timeline-gap${
                      dragState && gapFitsDrag(segment.duration, dragState)
                        ? " stage2-cal__timeline-gap--active"
                        : ""
                    }${
                      activeDropGap === segment.key
                        ? " stage2-cal__timeline-gap--target"
                        : ""
                    }`}
                    style={segmentFlexStyle(segment.duration)}
                    data-gap-start={gapStart ?? ""}
                    data-gap-duration={segment.duration}
                    data-gap-key={segment.key}
                    onDragOver={(e) => handleGapDragOver(e, segment)}
                    onDragLeave={() => setActiveDropGap(null)}
                    onDrop={(e) => handleGapDrop(e, segment)}
                  />
                ) : (
                  <ScheduleBlock
                    key={segment.key}
                    block={segment.block}
                    flexGrow={segment.duration}
                    showDurationStepper={isMobilePortrait}
                    onDurationChange={handleBlockDurationChange}
                    onRemove={handleRemove}
                  />
                );
              })}
            </div>
          </div>
          <div className="stage2-cal__tip-row">
            <p className="stage2-cal__tip">
              <span className="stage2-cal__tip-icon" aria-hidden>
                ✓
              </span>
              Tip: Drag activities to open slots. Shorten meetings first to create
              room for breaks and focus time.
            </p>
            <button
              type="button"
              className="stage2-cal__insights"
              onClick={() => setShowInsights((v) => !v)}
            >
              <span aria-hidden>📊</span> View Insights
            </button>
          </div>
          {showInsights ? (
            <div className="stage2-cal__insights-panel">
              {validation.missing.length ? (
                <ul>
                  {validation.missing.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p>Your schedule meets all productivity goals.</p>
              )}
            </div>
          ) : null}
        </section>

        <aside className="stage2-cal__sidebar stage2-cal__sidebar--right">
          {!isMobilePortrait ? (
            <div className="stage2-cal__focus-card">
              <div className="stage2-cal__focus-head">
                <span aria-hidden>🎯</span>
                <h3>Focus Block</h3>
              </div>
              <p>Protect deep work time for maximum productivity!</p>
              <div className="stage2-cal__focus-art" aria-hidden>
                <div className="stage2-cal__focus-cal">
                  <span>RESERVE</span>
                  <strong>FOCUS TIME</strong>
                  <em>20 min</em>
                </div>
                <span className="stage2-cal__focus-shield">✓</span>
              </div>
            </div>
          ) : null}

          {!isMobilePortrait ? (
            <div className="stage2-cal__metric-card">
              <h3 className="stage2-cal__panel-title stage2-cal__panel-title--bar">
                Wellness Boosters
              </h3>
              <ul className="stage2-cal__booster-list">
                <li className={validation.checks.hasWater ? "stage2-cal__check--ok" : ""}>
                  <span className="stage2-cal__booster-icon" aria-hidden>
                    💧
                  </span>
                  <span className="stage2-cal__booster-label">Water Break</span>
                  <span className="stage2-cal__booster-dur">10 min</span>
                </li>
                <li className={validation.checks.hasStretch ? "stage2-cal__check--ok" : ""}>
                  <span className="stage2-cal__booster-icon" aria-hidden>
                    🧘
                  </span>
                  <span className="stage2-cal__booster-label">Stretch</span>
                  <span className="stage2-cal__booster-dur">20 min</span>
                </li>
                <li className={validation.checks.hasWalkTalk ? "stage2-cal__check--ok" : ""}>
                  <span className="stage2-cal__booster-icon" aria-hidden>
                    🚶
                  </span>
                  <span className="stage2-cal__booster-label">Walk &amp; Talk</span>
                  <span className="stage2-cal__booster-dur">30 min</span>
                </li>
              </ul>
            </div>
          ) : null}

          <div className="stage2-cal__score-card">
            <h3 className="stage2-cal__panel-title stage2-cal__panel-title--bar">
              Schedule Score
            </h3>
            <div className="stage2-cal__score-body">
              <div
                className="stage2-cal__score-ring"
                style={{ "--score": validation.score }}
              >
                <span className="stage2-cal__score-num">{validation.score}</span>
              </div>
              <div className="stage2-cal__score-copy">
                <p className="stage2-cal__score-label">
                  {validation.valid ? "Great Balance!" : "Schedule Score"}
                </p>
                <p className="stage2-cal__score-hint">{scoreMessage}</p>
              </div>
            </div>
          </div>
        </aside>
      </div>

      {feedback ? (
        <p
          className={`stage2-cal__feedback stage2-cal__feedback--${feedback.type}`}
          role="alert"
        >
          {feedback.message}
        </p>
      ) : null}

      <footer className="stage2-cal__footer">
        <button type="button" className="stage2-cal__reset" onClick={handleReset}>
          <span aria-hidden>↺</span> Reset Schedule
        </button>
        <button
          type="button"
          className="stage2-cal__submit"
          disabled={submitting}
          onClick={handleSubmit}
        >
          {submitting ? "SAVING…" : "Submit Schedule ✓"}
        </button>
      </footer>
    </div>
  );
}
